/**
 * Walks on a day (Kurtis 2026-10-25, docs/plans/ADD_NOW_PLAN.md phase 3): a
 * walk is one stop of the day — a curated walk (walkId) or one you made
 * (ownWalk) — and its places are the day's own stops pointing at it
 * (`inWalk`, migration 322). Each place is reviewed, photographed and retimed
 * on its own; the walk is what lets several quick stops sit in one block.
 *
 * - The walk's time is its places': first place's minute to last place's end
 *   (`walkSpan`), kept in step by `walksInStep` in the planner's one mutation
 *   funnel, so every edit path (drag, agenda, edit modal) moves a walk and its
 *   places together without being taught. A stored span means every reader
 *   that knows one block (clashes, the today guess, the day bar) sees one.
 * - A planned walk lays its places out along the way at a stroll's pace
 *   (`layOutPlaces`): a quick stop takes no time, a stay its length.
 * - A walk being written down by "I'm here" is open (its own `addedVia:
 *   'here'`): the next "I'm here" joins it (`openHereWalk`) until it is ended or
 *   30 minutes pass after its last place.
 * - The offer: three of your "I'm here" stops in a row, in no walk, the three
 *   starting within 30 minutes (`strollRun`).
 *
 * Pure — no React, no i18n, no clock.
 */
import { stopKey } from './tripActivityOps';
import { isQuickStop, stopLengthMinutes, runningHereStop, endStopAt } from './quickStops';
import { stopFrame, LATE_NIGHT_CUTOFF } from './tripNow';
import { blockOfMinutes, rescheduleActivityToDate } from './activityUtilities';
import { distanceMeters } from './tourWalkability';
import { getActivityCoords } from './travelEstimates';
import { anchorForBlock, orderByNearest } from './bulkAddOrder';

const DAY = 24 * 60;
/** "I'm here" stops this close (start to start) make a run the offer asks about. */
export const STROLL_RUN_MINUTES = 30;
/** How many in a run before the offer. */
export const STROLL_RUN_COUNT = 3;
/** An open walk takes the next "I'm here" up to this long after its last place ends. */
export const OPEN_WALK_MINUTES = 30;
// A stroll's pace: the middle of a curated walk's range (walking through at
// 2.5 mph to a slow stroll at 1 mph — utils/walkLabels), ~38 m a minute; a
// straight line between two places is a quarter longer on the street (the
// walks runbook's ×1.25 for winding lanes).
const STROLL_M_PER_MIN = 1609 / 42;
const STREET_FACTOR = 1.25;
// Where a part of the day starts (utils/daySlots BLOCK_WINDOWS), for a walk dropped into one.
const BLOCK_STARTS = { morning: 9 * 60, afternoon: 13 * 60, evening: 18 * 60 };

const toMinutes = (time) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(time || ''));
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};
const toClock = (frameMin) => {
  const v = ((Math.round(frameMin) % DAY) + DAY) % DAY;
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};

/** A walk stop: a curated walk or one you made (never a walking tour, which carries its own tourStops). */
export function isWalk(a) {
  if (!a) return false;
  return !!(a.ownWalk || a.walkId || a.walk_id)
    || (a.activityType || a.activity_type) === 'walk'
    || a.category === 'walk';
}

/** A walk you made (no curated walk behind it). */
export const isOwnWalk = (a) => isWalk(a) && !(a.walkId || a.walk_id);

/** The walk a stop names as the one it is inside (its key), or null. */
export const walkKeyOf = (a) => (a && !isWalk(a) && (a.inWalk || a.in_walk_id)) || null;

// The order inside a walk: timed places by their minute (late night after the
// evening), then untimed ones as they lie on the day.
function placeOrder(places, indexOf) {
  return places.slice().sort((x, y) => {
    const fx = stopFrame(x);
    const fy = stopFrame(y);
    if (fx && fy && fx.start !== fy.start) return fx.start - fy.start;
    if (fx && !fy) return -1;
    if (!fx && fy) return 1;
    return indexOf.get(x) - indexOf.get(y);
  });
}

const cache = new WeakMap();

/**
 * One day's walks: `walks` (key → walk stop), `placesOf(walk)` (its places in
 * walking order), `walkOf(stop)` (the walk a stop is inside, when that walk is
 * on this day) and `isInside(stop)`. A stop naming a walk that isn't on the
 * day (removed, or not loaded yet) is inside nothing. Cached per array.
 */
export function dayWalks(activities) {
  const list = Array.isArray(activities) ? activities : [];
  if (cache.has(list)) return cache.get(list);
  const walks = new Map();
  list.forEach((a) => { if (isWalk(a)) { const k = stopKey(a); if (k) walks.set(k, a); } });
  const indexOf = new Map(list.map((a, i) => [a, i]));
  const byWalk = new Map();
  const walkOfStop = new Map();
  list.forEach((a) => {
    const k = walkKeyOf(a);
    if (!k || !walks.has(k)) return;
    if (!byWalk.has(k)) byWalk.set(k, []);
    byWalk.get(k).push(a);
    walkOfStop.set(a, walks.get(k));
  });
  byWalk.forEach((places, k) => byWalk.set(k, placeOrder(places, indexOf)));
  const out = {
    walks,
    placesOf: (walk) => byWalk.get(stopKey(walk)) || [],
    walkOf: (a) => walkOfStop.get(a) || null,
    isInside: (a) => walkOfStop.has(a)
  };
  cache.set(list, out);
  return out;
}

/**
 * What a stop is to its day's walks, for a list that names it (Kurtis
 * 2026-11-02): `{ isWalk, placeCount, walkTitle }` — a walk and how many
 * places it holds, or the name of the walk a place is inside (null when none).
 */
export function walkRole(a, activities) {
  const w = dayWalks(activities);
  const inside = w.walkOf(a);
  return {
    isWalk: isWalk(a),
    placeCount: isWalk(a) ? w.placesOf(a).length : 0,
    walkTitle: inside ? (inside.custom_title || inside.title || inside.name || null) : null
  };
}

/**
 * A list of a day's stops with each walk heading its places (Kurtis
 * 2026-11-02: on the recap the walk "La Rambla" and the place "La Rambla"
 * read the same). `items` keep their order, except that a place inside a walk
 * that is also in `items` moves to just after it, in walking order. Only
 * `items` come out: a place whose walk isn't listed stays where it was, in no
 * group. `activityOf(item)` is an item's stop on `activities` (default: the
 * item itself).
 *
 * → [{ item, walkPlaces, rail }]: `walkPlaces` the walk's places on the day
 * (a walk only, else null), `rail` 'first' | 'middle' | 'last' | 'only' for a
 * place shown under its walk, else null.
 */
export function walkGroups(items, activities, activityOf = (x) => x) {
  const list = items || [];
  const walks = dayWalks(activities);
  const listedWalks = new Set(list.map(activityOf).filter(a => a && isWalk(a)));
  const underWalk = (item) => {
    const w = walks.walkOf(activityOf(item));
    return w && listedWalks.has(w) ? w : null;
  };
  const out = [];
  list.forEach((item) => {
    if (underWalk(item)) return;
    const a = activityOf(item);
    if (!a || !isWalk(a)) { out.push({ item, walkPlaces: null, rail: null }); return; }
    const places = walks.placesOf(a);
    out.push({ item, walkPlaces: places, rail: null });
    const mine = list.filter(x => underWalk(x) === a)
      .sort((x, y) => places.indexOf(activityOf(x)) - places.indexOf(activityOf(y)));
    mine.forEach((x, i) => out.push({
      item: x,
      walkPlaces: null,
      rail: mine.length === 1 ? 'only' : i === 0 ? 'first' : i === mine.length - 1 ? 'last' : 'middle'
    }));
  });
  return out;
}

/** The day's stops with each walk's places folded into it: what a list of the day shows, one row per walk. */
export function foldWalks(activities) {
  const { isInside } = dayWalks(activities);
  return (activities || []).filter(a => !isInside(a));
}

/** A walk's span from its places, in the day's frame: { start, end }, or null when none is timed. */
export function walkSpan(places) {
  let start = null;
  let end = null;
  (places || []).forEach((p) => {
    const f = stopFrame(p);
    if (!f) return;
    if (start === null || f.start < start) start = f.start;
    if (end === null || f.end > end) end = f.end;
  });
  return start === null ? null : { start, end };
}

// The fields that put a stop at `frameMin` (its day's frame), its part of the
// day following it (a late-night minute stays on the evening).
// A night can't run past 05:59 on its day (06:00 is the next day's): a walk
// laid out late into the night stops its minutes there rather than wrapping
// round to this day's morning.
function atMinute(frameMin) {
  const at = Math.min(frameMin, DAY + LATE_NIGHT_CUTOFF - 1);
  const late = at >= DAY;
  return { scheduledTime: toClock(at), timeBlock: late ? 'evening' : blockOfMinutes(at % DAY) };
}

/**
 * The walk's own time and length for a span: at its start, its length the
 * span (none — a quick stop — when 0). Its part of the day changes only when
 * it must: a walk with none of its own keeps none (its time says it), unless
 * it runs late at night.
 */
export function spanFields(span, walk = null) {
  const len = Math.max(0, Math.round(span.end - span.start));
  const at = atMinute(span.start);
  const own = walk ? (walk.timeBlock || walk.time_block || null) : at.timeBlock;
  const late = span.start >= DAY && span.start - DAY < LATE_NIGHT_CUTOFF;
  const timeBlock = own || late ? at.timeBlock : null;
  return len > 0
    ? { scheduledTime: at.scheduledTime, timeBlock, duration: len, durationHours: undefined, durationMinutes: undefined, quickStop: false }
    : { scheduledTime: at.scheduledTime, timeBlock, duration: null, durationHours: undefined, durationMinutes: undefined, quickStop: true };
}

const sameSpanAs = (walk, fields) => walk.scheduledTime === fields.scheduledTime
  && !!walk.quickStop === fields.quickStop
  && (fields.quickStop || Number(walk.duration) === fields.duration)
  && (walk.timeBlock || walk.time_block || null) === fields.timeBlock;

/** Minutes of strolling between two places (straight line × 1.25), 0 when either has no position. */
export function strollMinutesBetween(a, b) {
  const m = distanceMeters(getActivityCoords(a), getActivityCoords(b));
  return m ? Math.max(1, Math.round((m * STREET_FACTOR) / STROLL_M_PER_MIN)) : 0;
}

/** Minutes of strolling `meters` along a walk's own line. */
export const strollMinutes = (meters) => (meters > 0 ? Math.max(1, Math.round(meters / STROLL_M_PER_MIN)) : 0);

/**
 * A planned walk's places laid out from `startMin` (day frame), in the order
 * given: each at the minute the walk reaches it — a quick stop takes no time,
 * a stay its length — then the stroll to the next (`legMinutes[i]`: from
 * place i to i + 1; else straight line between them). → [{ place, patch,
 * minute }] (`minute`: the day-frame minute it is reached).
 */
export function layOutPlaces(startMin, places, { legMinutes = null } = {}) {
  let t = startMin;
  return (places || []).map((place, i) => {
    const patch = atMinute(t);
    const stay = isQuickStop(place) || place.quickStop ? 0 : stopLengthMinutes(place, 0);
    const next = places[i + 1];
    const leg = next ? (legMinutes && Number.isFinite(legMinutes[i]) ? legMinutes[i] : strollMinutesBetween(place, next)) : 0;
    const minute = t;
    t += stay + leg;
    return { place, patch, minute };
  });
}

/**
 * The stroll between consecutive places of a curated walk, along its own line
 * (`path`, [[lat, lng], …]): each place at the line's nearest point. →
 * minutes per leg (null for a place with no position), or null with no line.
 */
export function walkLegMinutes(path, places) {
  const line = lineMeasure(path);
  if (!line) return null;
  const at = line.inOrder(places || []);
  return (places || []).slice(1).map((_, i) => (at[i] !== null && at[i + 1] !== null ? strollMinutes(Math.abs(at[i + 1] - at[i])) : null));
}

/**
 * A curated walk's line the way its places are walked (Kurtis 2026-11-02: a
 * walk has no right way round — "Start at …" takes it from either end): as
 * drawn, or end to start when its places, in walking order, run back along
 * it by more than FLIP_MARGIN_M. Nothing stores the way round; the places'
 * order says it. A place at a loop's start/finish says nothing (it is either
 * end). As drawn with fewer than two places that say something; null with no
 * line.
 */
export function walkLineOf(walk, places) {
  const path = walk?.walkPath;
  if (!Array.isArray(path) || path.length < 2) return null;
  const start = { lat: path[0][0], lng: path[0][1] };
  const finish = { lat: path[path.length - 1][0], lng: path[path.length - 1][1] };
  const loop = distanceMeters(start, finish) <= CLOSURE_M;
  const placed = (places || []).filter((p) => {
    const c = getActivityCoords(p);
    return c && !(loop && distanceMeters(c, start) <= CLOSURE_M);
  });
  if (placed.length < 2) return path;
  const back = path.slice().reverse();
  return backtrackMeters(path, placed) - backtrackMeters(back, placed) > FLIP_MARGIN_M ? back : path;
}

/** Whether `places`, in their order, walk a curated walk's line end to start (walkLineOf). */
const readsBack = (walk, places) => {
  const line = walkLineOf(walk, places);
  return !!line && line !== walk.walkPath;
};

// A loop's start and finish this near each other close it; a place this near
// them could be at either end.
const CLOSURE_M = 60;
// Places a step or two out of line (a landmark just before the stop beside
// it) are no walk the other way round: it must step back this much less.
const FLIP_MARGIN_M = 100;

// How far places, in their order, step back along a line (metres).
function backtrackMeters(path, places) {
  let back = 0;
  let prev = null;
  lineMeasure(path).inOrder(places).forEach((at) => {
    if (at === null) return;
    if (prev !== null && at < prev) back += prev - at;
    prev = at;
  });
  return back;
}

// A walk's line ([[lat, lng], …]) measured: its length, and how far along it a
// place is (at the line's nearest point; null with no position). null with no line.
// ~15 m, squared, in degrees (the projection's units): "about as near".
const NEAR_SQ = (15 / 111000) ** 2;
// metersAt's `after` asking for the distance off the line instead.
const OFF_LINE = Symbol('offLine');
function lineMeasure(path) {
  if (!Array.isArray(path) || path.length < 2) return null;
  const pt = ([lat, lng]) => ({ lat, lng });
  const along = [0];
  for (let i = 1; i < path.length; i++) along.push(along[i - 1] + (distanceMeters(pt(path[i - 1]), pt(path[i])) || 0));
  return {
    length: along[along.length - 1],
    // The nearest point on any segment (a flat projection, longitude shrunk
    // by the latitude — fine at a walk's scale), measured along the line. Of
    // points about as near (within ~15 m: a loop's start and finish, a line
    // crossing itself), the first at or after `after` metres along.
    metersAt: (place, after = 0) => {
      const c = getActivityCoords(place);
      if (!c) return null;
      const k = Math.cos((c.lat * Math.PI) / 180);
      const candidates = [];
      for (let i = 1; i < path.length; i++) {
        const [aLat, aLng] = path[i - 1];
        const [bLat, bLng] = path[i];
        const dx = (bLng - aLng) * k;
        const dy = bLat - aLat;
        const len2 = dx * dx + dy * dy;
        const tt = len2 ? Math.max(0, Math.min(1, (((c.lng - aLng) * k) * dx + (c.lat - aLat) * dy) / len2)) : 0;
        const px = aLng * k + tt * dx;
        const py = aLat + tt * dy;
        candidates.push({ d: (c.lng * k - px) ** 2 + (c.lat - py) ** 2, at: along[i - 1] + tt * (along[i] - along[i - 1]) });
      }
      const nearest = Math.min(...candidates.map(x => x.d));
      if (after === OFF_LINE) return Math.sqrt(nearest) * 111000;
      const near = candidates.filter(x => x.d <= nearest + NEAR_SQ).sort((x, y) => x.at - y.at);
      return (near.find(x => x.at >= after - 1) || near[0]).at;
    },
    // How far a place is from the line (metres, at its nearest point); null with no position.
    metersOff(place) { return this.metersAt(place, OFF_LINE); },
    // Places in walking order, each measured at or after the one before.
    inOrder(places) {
      let after = 0;
      return places.map((p) => {
        const at = this.metersAt(p, after);
        if (at !== null) after = at;
        return at;
      });
    }
  };
}

/**
 * A curated walk's line beyond its places (Kurtis 2026-10-26): the stroll from
 * the line's start to its first place (`lead`) and from its last place to the
 * line's end (`tail`), in minutes — so a walk with few of our places along it
 * still holds its whole length on the day. { lead: 0, tail: 0 } for a walk
 * with no line (one you made) or places with no position.
 */
export function walkLineEnds(walk, places) {
  const timed = (places || []).filter(p => stopFrame(p));
  const line = isWalk(walk) && (walk.walkId || walk.walk_id) ? lineMeasure(walkLineOf(walk, timed)) : null;
  if (!line || !timed.length) return { lead: 0, tail: 0 };
  const at = line.inOrder(timed);
  // Only from a first / last place with a position: one without is no point on the line.
  const first = at[0];
  const last = at[at.length - 1];
  return {
    lead: first !== null ? strollMinutes(first) : 0,
    tail: last !== null ? strollMinutes(Math.max(0, line.length - last)) : 0
  };
}

/** Minutes of strolling from a curated walk's line start to `place` (0 with no line or position). */
export function walkMinutesTo(path, place) {
  const line = lineMeasure(path);
  const at = line ? line.metersAt(place, 0) : null;
  return at ? strollMinutes(at) : 0;
}

/** A walk's span on its day: its places', and for a curated walk its whole line (walkLineEnds). */
export function walkSpanOf(walk, places) {
  const span = walkSpan(places);
  if (!span) return null;
  const { lead, tail } = walkLineEnds(walk, places);
  return { start: Math.max(0, span.start - lead), end: span.end + tail };
}

// "Walking through" — a brisk 5 km/h along the straight line between places
// (utils/tourWalkability): the least time the walking can take.
const BRISK_M_PER_MIN = 83;

/**
 * A walk planned shorter than its walking can take (Kurtis 2026-10-26: "places
 * 5 km apart but only 30 minutes" is a caution, never a change): the least
 * walking — a curated walk's own length, else the straight line between its
 * places in walking order at a brisk pace — against its span. A walk written
 * down by "I'm here" has real times (a tram between two places is no
 * mistake): no caution. → { minimum, planned } (minutes) when the plan is
 * short by more than 5 minutes of walking of at least 15, else null.
 */
export function walkTimeCaution(walk, places) {
  if ((walk?.addedVia || walk?.added_via) === 'here' || (places || []).some(p => (p?.addedVia || p?.added_via) === 'here')) return null;
  const span = walkSpanOf(walk, places);
  if (!span) return null;
  const lengthM = Number(walk?.lengthM || walk?.length_m);
  const timed = (places || []).filter(p => stopFrame(p));
  let walking = 0;
  if ((walk?.walkId || walk?.walk_id) && lengthM > 0) walking = lengthM / BRISK_M_PER_MIN;
  else {
    for (let i = 1; i < timed.length; i++) {
      walking += (distanceMeters(getActivityCoords(timed[i - 1]), getActivityCoords(timed[i])) || 0) / BRISK_M_PER_MIN;
    }
  }
  const minimum = Math.round(walking);
  const planned = Math.round(span.end - span.start);
  return minimum >= 15 && planned + 5 < minimum ? { minimum, planned } : null;
}

/**
 * Several places added together as a walk (the map's Select places → "As a
 * walk", mockup 4A): nearest first from the stop before `startMin` on the day,
 * each a quick stop, laid out from `startMin` (day frame) at a stroll's pace.
 * → { ordered: [{ item, minute, time }], end } (`end`: the last place's minute).
 */
export function bulkWalkPlan(items, dayActivities, startMin) {
  const block = startMin >= DAY ? 'evening' : blockOfMinutes(startMin % DAY);
  const ordered = orderByNearest(items || [], anchorForBlock(dayActivities || [], block));
  const laid = layOutPlaces(startMin, ordered.map(item => ({ ...item, quickStop: true, duration: null })));
  return {
    ordered: laid.map(({ patch, minute }, i) => ({ item: ordered[i], minute, time: patch.scheduledTime, timeBlock: patch.timeBlock })),
    end: laid.length ? laid[laid.length - 1].minute : startMin
  };
}

const sameDayKey = (d) => (d ? `${d.date || ''}|${d.plan_variant || 'A'}` : '');
// A day as itself (its uid; else its date and plan), for "did this stop stay on its day".
const dayKeyOf = (d) => (d ? (d.uid || sameDayKey(d)) : '');

/**
 * The days after an edit, with every walk in step with its places (the
 * mutation funnel's normalizer; returns `next` itself when nothing moved):
 *
 * - a walk moved to another day takes its places along;
 * - a walk given a new time while its places kept theirs (dragged, retimed)
 *   moves its places by the same amount;
 * - then each walk's time and length are its places' span;
 * - a walk you made that had places and has none left is removed.
 */
export function walksInStep(prevDays, nextDays) {
  if (!Array.isArray(nextDays)) return nextDays;
  const prevWalks = new Map();
  const prevPlaces = new Map();
  (prevDays || []).forEach((day) => {
    const acts = day?.activities || [];
    const { walks } = dayWalks(acts);
    walks.forEach((w, k) => prevWalks.set(k, { walk: w, day }));
    acts.forEach((a) => {
      const k = walkKeyOf(a);
      if (k) prevPlaces.set(stopKey(a), a);
    });
  });

  let days = nextDays;
  let changed = false;
  const setDay = (i, activities) => {
    if (days === nextDays) days = nextDays.slice();
    days[i] = { ...days[i], activities };
    changed = true;
  };

  // 1. A place on another day than its walk: when the walk moved there and
  // the place stayed where it was, the place follows it; a place that moved
  // itself (on its own, or kept on its date by a day move — pinFixedPlans)
  // leaves the walk. Every move is decided from `nextDays` first, then made.
  const prevDayOfStop = new Map();
  (prevDays || []).forEach((day) => (day?.activities || []).forEach((a) => {
    const k = stopKey(a);
    if (k) prevDayOfStop.set(k, dayKeyOf(day));
  }));
  const walkDay = new Map();
  nextDays.forEach((day, i) => (day?.activities || []).forEach((a) => { if (isWalk(a)) walkDay.set(stopKey(a), i); }));
  const follow = new Map(); // place → target day index
  const leave = new Set();
  nextDays.forEach((day, i) => (day?.activities || []).forEach((a) => {
    const k = walkKeyOf(a);
    if (!k || !walkDay.has(k) || walkDay.get(k) === i) return;
    const stayedPut = prevDayOfStop.get(stopKey(a)) === dayKeyOf(day);
    const walkMoved = !prevWalks.has(k) || dayKeyOf(prevWalks.get(k).day) !== dayKeyOf(nextDays[walkDay.get(k)]);
    if (stayedPut && walkMoved) follow.set(a, walkDay.get(k));
    else leave.add(a);
  }));
  if (follow.size || leave.size) {
    days = nextDays.map((day, i) => {
      const acts = day?.activities || [];
      const kept = acts.filter(a => !follow.has(a)).map(a => (leave.has(a) ? { ...a, inWalk: null } : a));
      const arriving = [...follow].filter(([, to]) => to === i).map(([a]) => (
        day.date && a.scheduledDate && a.scheduledDate !== day.date ? rescheduleActivityToDate(a, day.date) : a
      ));
      return kept.length === acts.length && !arriving.length && ![...leave].some(a => acts.includes(a))
        ? day : { ...day, activities: [...kept, ...arriving] };
    });
    changed = true;
  }

  // 2–4. Per day: walks moved in time take their places; spans; empty walks you made go.
  days.forEach((day, i) => {
    let acts = day?.activities || [];
    const { walks, placesOf } = dayWalks(acts);
    if (!walks.size) return;
    let dayChanged = false;
    const replace = (from, to) => { acts = acts.map(a => (a === from ? to : a)); dayChanged = true; };
    walks.forEach((walk, k) => {
      const places = placesOf(walk);
      const before = prevWalks.get(k)?.walk;
      if (before && places.length) {
        const was = stopFrame(before);
        const now = stopFrame(walk);
        const placesUnmoved = places.every((p) => {
          const old = prevPlaces.get(stopKey(p));
          return old && old.scheduledTime === p.scheduledTime;
        });
        // Dropped into another part of the day (no time of its own now): it
        // starts where that part does.
        const block = walk.timeBlock || walk.time_block;
        const target = now ? now.start
          : (was && !walk.scheduledTime && BLOCK_STARTS[block] != null && block !== (before.timeBlock || before.time_block || blockOfMinutes(was.start % DAY))
            ? BLOCK_STARTS[block] : null);
        if (was && target !== null && was.start !== target && placesUnmoved) {
          const delta = target - was.start;
          places.forEach((p) => {
            const f = stopFrame(p);
            if (f) replace(p, { ...p, ...atMinute(Math.max(0, f.start + delta)) });
          });
        }
      }
    });
    // Each walk's time and length: its places' span, as they are now.
    const fresh = dayWalks(acts);
    fresh.walks.forEach((walk) => {
      const span = walkSpanOf(walk, fresh.placesOf(walk));
      if (!span) return;
      const fields = spanFields(span, walk);
      if (!sameSpanAs(walk, fields)) replace(walk, { ...walk, ...fields });
    });
    // A walk you made, emptied by this edit, goes with its last place.
    const after = dayWalks(acts);
    after.walks.forEach((walk, k) => {
      if (!isOwnWalk(walk) || after.placesOf(walk).length) return;
      const hadPlaces = [...prevPlaces.values()].some(p => walkKeyOf(p) === k);
      if (hadPlaces) { acts = acts.filter(a => a !== walk); dayChanged = true; }
    });
    if (dayChanged) setDay(i, acts);
  });

  return changed ? days : nextDays;
}

/**
 * The walk the next "I'm here" joins: a walk open by "I'm here" and not
 * ended (its own addedVia 'here' — written down as you went, or a planned
 * walk you started, phase 4), whose last place ended no more than
 * OPEN_WALK_MINUTES before `nowMinutes` (day frame). `place`: the place being
 * added — a planned walk takes only one near it (JOIN_WALK_METERS, decision
 * 20); a walk written down takes any. → the walk or null.
 */
export function openHereWalk(day, nowMinutes, { place = null } = {}) {
  if (!Number.isFinite(nowMinutes)) return null;
  const acts = day?.activities || [];
  const { walks, placesOf } = dayWalks(acts);
  let best = null;
  let bestEnd = -Infinity;
  walks.forEach((walk) => {
    if (!isReached(walk)) return;
    const places = placesOf(walk);
    const span = walkSpan(places);
    if (!span || span.start > nowMinutes || nowMinutes - span.end > OPEN_WALK_MINUTES) return;
    if (place && isPlannedWalk(walk, places) && !(metersToWalk(walk, places, place) <= JOIN_WALK_METERS)) return;
    if (span.end > bestEnd) { best = walk; bestEnd = span.end; }
  });
  return best;
}

// ---- Walking it live (Kurtis 2026-10-27, docs/plans/ADD_NOW_PLAN.md phase 4) ----

/**
 * A walk being walked with nothing planned ahead (decision 21): open by
 * "I'm here", its last place already begun, no more than
 * OPEN_WALK_MINUTES ago (`nowMinutes`, day frame). Its end reads "now", not
 * its last place's minute; a planned walk being walked keeps its planned end.
 */
export function isWalkingNow(walk, places, nowMinutes) {
  if (!Number.isFinite(nowMinutes) || !isWalk(walk) || !isReached(walk)) return false;
  const span = walkSpan(places);
  // Begun, not ended: the last place's length is a default (half an hour for
  // an "I'm here"), not a plan ahead of you.
  const lastStart = Math.max(...(places || []).map(p => stopFrame(p)?.start ?? -Infinity));
  return !!span && lastStart <= nowMinutes && nowMinutes - span.end <= OPEN_WALK_MINUTES;
}

/** A planned walk being walked takes an "I'm here" this near its places or its line. */
export const JOIN_WALK_METERS = 300;

/** A stop you said "I'm here" at (made by it, or a planned one started by it): reached. */
export const isReached = (a) => (a?.addedVia || a?.added_via) === 'here';

// A walk with a plan ahead of you: a curated one, or one with places not reached.
const isPlannedWalk = (walk, places) => !!(walk.walkId || walk.walk_id) || places.some(p => !isReached(p));

// Metres from `place` to a walk: its nearest place, or its line. Infinity when unknown.
function metersToWalk(walk, places, place) {
  const c = getActivityCoords(place);
  if (!c) return Infinity;
  let best = Infinity;
  const consider = (d) => { if (d !== null && d < best) best = d; };
  places.forEach(p => consider(distanceMeters(c, getActivityCoords(p))));
  const line = (walk.walkId || walk.walk_id) ? lineMeasure(walk.walkPath) : null;
  if (line) consider(line.metersOff(place));
  return best;
}

/**
 * Where a place you came to falls in a walk (decision 20): an index into
 * `places` (walking order), never before `after` + 1 (the last place
 * reached). Along a curated walk's line: before the first place further along
 * it; else where it adds the least walking. With no position: right after
 * the last place reached.
 */
export function walkInsertIndex(walk, places, place, after = -1) {
  const from = after + 1;
  const list = places || [];
  if (!getActivityCoords(place)) return from;
  const line = (walk?.walkId || walk?.walk_id) ? lineMeasure(walkLineOf(walk, list)) : null;
  if (line) {
    const at = line.inOrder(list);
    const mine = line.metersAt(place, after >= 0 && at[after] !== null ? at[after] : 0);
    for (let i = from; i < list.length; i++) if (at[i] !== null && at[i] > mine) return i;
    return list.length;
  }
  const d = (a, b) => distanceMeters(getActivityCoords(a), getActivityCoords(b));
  let best = from;
  let bestCost = Infinity;
  for (let g = from; g <= list.length; g++) {
    const prev = g > 0 ? list[g - 1] : null;
    const next = list[g] || null;
    const toPrev = prev ? d(prev, place) : 0;
    const toNext = next ? d(place, next) : 0;
    const between = prev && next ? d(prev, next) : 0;
    if (toPrev === null || toNext === null || between === null) continue;
    const cost = toPrev + toNext - between;
    if (cost < bestCost - 1e-6) { best = g; bestCost = cost; }
  }
  return best;
}

// The fields Undo puts back on a stop "I'm here" changed, and how it tells
// the stop still stands as "I'm here" left it (as a re-read gives it back:
// a time with seconds, a length as a string, hours and minutes worked out).
const HERE_FIELDS = ['scheduledTime', 'timeBlock', 'duration', 'durationHours', 'durationMinutes', 'quickStop', 'addedVia'];
const fieldsOf = (a) => Object.fromEntries(HERE_FIELDS.map(f => [f, a[f] === undefined ? null : a[f]]));
// A walk's own time and length are its places' span (walksInStep re-spans it
// after the edit): only its mark tells it, and only its mark goes back —
// putting its old time back would move its places along with it.
const sameAsLeft = (a, after) => isReached(a) === (after.addedVia === 'here') && (isWalk(a) || (
  String(a.scheduledTime || '').slice(0, 5) === String(after.scheduledTime || '').slice(0, 5)
  && !!a.quickStop === !!after.quickStop
  && (after.quickStop || (Number(a.duration) || null) === (Number(after.duration) || null))));

/**
 * "I'm here" at a stop of today as one change (phase 4, decisions 17–20).
 * `key`: the stop. A planned stop starts at `nowMinutes` (day frame) and
 * keeps its length; `fresh`: a stop just added at this minute (the pin card's
 * add), already timed and marked. Anyone on the trip may start any stop
 * (decision 18): the marks are shared with the trip's travellers, so a place
 * a co-traveller reached reads as reached and stays where it is.
 *
 * - The stop you were at (your running "I'm here" stop) ends where you left
 *   it: at this minute, or — when places of a walk were passed on the way —
 *   where the walk to them began.
 * - Inside a walk: the walk is started (open, it takes the next "I'm here").
 *   Its places after this one, not reached yet, are laid out again from here
 *   at a stroll; the ones passed since the last place reached are spread
 *   along the way between, in their order. A fresh stop goes where it falls
 *   along the walk (walkInsertIndex). Reached places, and every other stop,
 *   never move.
 *
 * → { days, changes: [{ key, before, after }] (each stop it changed but a
 *   fresh one; Undo puts `before` back while `after` still stands —
 *   undoHereDays), ended: { title, quick, endedAt } | null, walk: { key,
 *   title } | null, passed: n }. `days` is the input itself when nothing changed.
 */
export function arriveDays(days, dayIndex, key, nowMinutes, { fresh = false } = {}) {
  const none = { days, changes: [], ended: null, walk: null, passed: 0 };
  const day = days?.[dayIndex];
  const acts = day?.activities || [];
  const arrived = acts.find(a => stopKey(a) === key);
  if (!arrived || isWalk(arrived) || !Number.isFinite(nowMinutes)) return none;
  const patches = new Map();
  const patch = (a, p) => patches.set(a, { ...(patches.get(a) || {}), ...p });
  if (!fresh) patch(arrived, { ...atMinute(nowMinutes), addedVia: 'here' });
  const running = runningHereStop(day, nowMinutes, { exclude: arrived });
  let leave = nowMinutes;
  let passedCount = 0;
  const { walkOf, placesOf } = dayWalks(acts);
  const walk = walkOf(arrived);
  if (walk) {
    if (!isReached(walk)) patch(walk, { addedVia: 'here' });
    const inOrder = placesOf(walk);
    const others = inOrder.filter(p => p !== arrived);
    let last = -1;
    others.forEach((p, i) => { if (isReached(p)) last = i; });
    const at = fresh ? walkInsertIndex(walk, others, arrived, last) : inOrder.indexOf(arrived);
    const passed = at > last ? others.slice(last + 1, at) : [];
    const ahead = others.slice(Math.max(at, last + 1));
    // The way round from every place (a fresh one's spot isn't known yet):
    // without the one arrived at, a loop's places can say nothing.
    const line = walkLineOf(walk, fresh ? others : inOrder);
    const legsOf = (seq) => {
      const along = line ? walkLegMinutes(line, seq) : null;
      return seq.slice(1).map((p, i) => (along && Number.isFinite(along[i]) ? along[i] : strollMinutesBetween(seq[i], p)));
    };
    // Ahead: laid out again from here.
    const seqAhead = [arrived, ...ahead];
    layOutPlaces(nowMinutes, seqAhead, { legMinutes: legsOf(seqAhead) })
      .slice(1)
      .forEach(({ place, patch: p }) => { if (p.scheduledTime !== place.scheduledTime) patch(place, p); });
    // Passed: along the way from the last place reached (or, with none, from
    // as far back as the walk to here takes) — its legs and the passed
    // places' own stays, fitted into the time it really took.
    if (passed.length) {
      passedCount = passed.length;
      const anchor = last >= 0 ? others[last] : null;
      const seq = [...(anchor ? [anchor] : []), ...passed, arrived];
      const legs = legsOf(seq);
      const offsets = [0];
      for (let k = 1; k < seq.length; k++) {
        const prev = seq[k - 1];
        const stay = prev !== anchor && !isQuickStop(prev) ? stopLengthMinutes(prev, 0) : 0;
        offsets.push(offsets[k - 1] + stay + legs[k - 1]);
      }
      const total = offsets[offsets.length - 1];
      let from = Math.max(0, nowMinutes - total);
      if (anchor) {
        const f = stopFrame(anchor);
        if (f) from = f.end;
      }
      // The stop you were at (in this walk or not) still running by the plan:
      // you left it when the walk here began, and the places passed come after it.
      const rf = running ? stopFrame(running) : null;
      if (rf) {
        leave = Math.max(rf.start, Math.min(rf.end, nowMinutes - total));
        from = leave;
      }
      from = Math.min(from, nowMinutes);
      const scale = total > 0 ? (nowMinutes - from) / total : 0;
      const first = anchor ? 1 : 0;
      passed.forEach((p, j) => {
        const off = total > 0 ? offsets[first + j] * scale : ((nowMinutes - from) * (j + 1)) / (passed.length + 1);
        const fields = atMinute(Math.round(from + off));
        if (fields.scheduledTime !== p.scheduledTime) patch(p, fields);
      });
    }
  }
  let ended = null;
  if (running) {
    const p = endStopAt(running, leave);
    if (p) {
      patch(running, p);
      ended = { title: running.title || running.name || '', quick: !!p.quickStop, endedAt: toClock(leave) };
    }
  }
  const walkInfo = walk ? { key: stopKey(walk), title: walk.title || walk.name || '' } : null;
  if (!patches.size) return { ...none, walk: walkInfo };
  const out = days.slice();
  out[dayIndex] = { ...day, activities: acts.map(a => (patches.has(a) ? { ...a, ...patches.get(a) } : a)) };
  const changes = [...patches].filter(([a]) => !(fresh && a === arrived)).map(([a, p]) => ({
    key: stopKey(a),
    before: fieldsOf(a),
    after: fieldsOf({ ...a, ...p })
  }));
  return { days: out, changes, ended, walk: walkInfo, passed: passedCount };
}

/**
 * Undo of an "I'm here" (arriveDays): each stop it changed goes back to
 * `before` — unless it has been changed since (its fields no longer
 * `after`): that change stands. `remove`: the key of a fresh stop to take off.
 * → the days, or `days` itself when nothing was put back.
 */
export function undoHereDays(days, dayIndex, changes, { remove = null } = {}) {
  const day = days?.[dayIndex];
  const acts = day?.activities || [];
  const byKey = new Map((changes || []).map(c => [c.key, c]));
  let changed = false;
  const next = [];
  acts.forEach((a) => {
    const k = stopKey(a);
    if (remove && k === remove) { changed = true; return; }
    const c = byKey.get(k);
    if (c && sameAsLeft(a, c.after)) {
      changed = true;
      next.push({ ...a, ...(isWalk(a) ? { addedVia: c.before.addedVia } : c.before) });
      return;
    }
    next.push(a);
  });
  if (!changed) return days;
  const out = days.slice();
  out[dayIndex] = { ...day, activities: next };
  return out;
}

/**
 * The run the offer asks about ("make these a walk?"): the trip's "I'm here"
 * stops on `day` in no walk (anyone's — the marks are shared; MapTab offers it
 * on the phone that made the run's last one), in a row by start, each starting within
 * STROLL_RUN_MINUTES of the one before, the last three within
 * STROLL_RUN_MINUTES of each other and the last started no more than that
 * before `nowMinutes`. → the run's stops (≥ STROLL_RUN_COUNT), or null.
 */
export function strollRun(day, nowMinutes) {
  if (!Number.isFinite(nowMinutes)) return null;
  const acts = day?.activities || [];
  const { isInside } = dayWalks(acts);
  const here = acts
    .filter(a => a && !isWalk(a) && !isInside(a) && (a.addedVia || a.added_via) === 'here' && stopFrame(a))
    .sort((x, y) => stopFrame(x).start - stopFrame(y).start);
  if (here.length < STROLL_RUN_COUNT) return null;
  const run = [here[here.length - 1]];
  for (let i = here.length - 2; i >= 0; i--) {
    if (stopFrame(run[0]).start - stopFrame(here[i]).start > STROLL_RUN_MINUTES) break;
    run.unshift(here[i]);
  }
  if (run.length < STROLL_RUN_COUNT) return null;
  const last = stopFrame(run[run.length - 1]).start;
  const third = stopFrame(run[run.length - STROLL_RUN_COUNT]).start;
  if (last - third > STROLL_RUN_MINUTES || nowMinutes - last > STROLL_RUN_MINUTES || nowMinutes < last) return null;
  return run;
}

/**
 * Whether the stroll offer's run (`keys`) can still be made a walk on `day`:
 * every stop still there and none inside a walk yet — a co-traveller may have
 * made it one first (the marks are shared). Partly taken, it is not made:
 * a walk of the leftovers would be a walk of one, named after a place in
 * someone else's.
 */
export function runStillFree(day, keys) {
  const acts = day?.activities || [];
  const { isInside } = dayWalks(acts);
  const byKey = new Map(acts.map(a => [stopKey(a), a]));
  return (keys || []).length > 0 && keys.every((k) => { const a = byKey.get(k); return !!a && !isInside(a); });
}

/**
 * A new walk you made holding `places` (already on the day): the walk stop's
 * fields (give it its minted ids) and each place's patch. `open`: made by
 * "I'm here" — the next one joins it. → { walk, patches: Map(place → patch) }.
 */
export function walkFromPlaces(places, { title, open = false } = {}) {
  const span = walkSpan(places);
  const walk = {
    ownWalk: true,
    activityType: 'walk',
    category: 'walk',
    title,
    name: title,
    ...(span ? spanFields(span) : {}),
    ...(open ? { addedVia: 'here' } : {})
  };
  return { walk, span };
}

/** Minutes of a walk's places that are stays (not quick), for "4 places · 20 min of stays". */
export function stayMinutes(places) {
  return (places || []).reduce((sum, p) => sum + (isQuickStop(p) ? 0 : stopLengthMinutes(p, 0)), 0);
}

export { toMinutes as walkTimeToMinutes };

/**
 * A walk card's own menu as one change to the days (one undo): 'ungroup' —
 * the walk goes, its places stay on the day as they are; 'removeWithPlaces'
 * — the walk and its places go; 'rename' — a walk you made takes
 * `opts.title`; 'reverse' — the walk from its other end (reverseWalkStart).
 * → the days, or `days` itself when the walk isn't on that day.
 */
export function walkActionDays(days, dayIndex, walkKey, action, opts = {}) {
  const day = days?.[dayIndex];
  const acts = day?.activities || [];
  const walk = acts.find(a => isWalk(a) && stopKey(a) === walkKey);
  if (!walk) return days;
  const inside = (a) => walkKeyOf(a) === walkKey;
  let next;
  if (action === 'reverse') {
    const patches = reversedTimes(walk, dayWalks(acts).placesOf(walk));
    if (!patches) return days;
    next = acts.map(a => (patches.has(a) ? { ...a, ...patches.get(a) } : a));
  } else if (action === 'ungroup') next = acts.filter(a => a !== walk).map(a => (inside(a) ? { ...a, inWalk: null } : a));
  else if (action === 'removeWithPlaces') next = acts.filter(a => a !== walk && !inside(a));
  else if (action === 'rename') {
    const title = typeof opts.title === 'string' ? opts.title.trim() : '';
    if (!title || !isOwnWalk(walk) || title === walk.title) return days;
    next = acts.map(a => (a === walk ? { ...a, title, name: title } : a));
  } else return days;
  const out = days.slice();
  out[dayIndex] = { ...day, activities: next };
  return out;
}

/**
 * The place a planned walk would start at taken the other way round ("Start
 * at …", Kurtis 2026-11-02): its last timed place, when it can be turned
 * (reversedTimes). → the place, or null.
 */
export function reverseWalkStart(walk, places) {
  const timed = (places || []).filter(p => stopFrame(p));
  return reversedTimes(walk, timed) ? timed[timed.length - 1] : null;
}

// The walk the other way round, from its start: its timed places last to
// first, each keeping its stay and the gap it had to its neighbour (none
// where a stay ran into the next place), from the stroll along the line
// from its other end. → Map(place → patch), or null when it can't be turned:
// fewer than two timed places, one reached by "I'm here" (a walk under way
// keeps its way), or a curated walk its places wouldn't read the other way
// round (walkLineOf: too close, or only a loop's start/finish placed) —
// its line's ends would land on the wrong side.
function reversedTimes(walk, places) {
  const timed = (places || []).filter(p => stopFrame(p));
  if (timed.length < 2 || isReached(walk) || timed.some(isReached)) return null;
  const back = timed.slice().reverse();
  const line = (walk.walkId || walk.walk_id) ? walkLineOf(walk, timed) : null;
  if (line && readsBack(walk, timed) === readsBack(walk, back)) return null;
  const from = line ? line.slice().reverse() : null;
  const lead = from && getActivityCoords(back[0]) ? walkMinutesTo(from, back[0]) : 0;
  let t = walkSpanOf(walk, timed).start + lead;
  return new Map(back.map((p, i) => {
    const at = t;
    const f = stopFrame(p);
    const next = back[i + 1];
    if (next) {
      const g = stopFrame(next);
      // Never level with the next: a tie reads in the day's order, not the walk's.
      let gap = Math.max(0, f.start - g.end);
      if (f.end - f.start + gap === 0 && f.start !== g.start) gap = 1;
      t = at + (f.end - f.start) + gap;
    }
    return [p, atMinute(at)];
  }));
}

/**
 * A new walk you made holding the stops `placeKeys` of one day, as one change:
 * the walk goes in just before its first place, each place points at it, and
 * the funnel (walksInStep) gives the walk its places' span. `walkId`: its
 * minted id. `open`: made by "I'm here" — the next one joins it. `layOut`:
 * lay the places out at a stroll's pace in the order given, from `start`
 * (day-frame minutes; else their first one's minute) — a walk planned from
 * the day's stops, whose `stays` (key → minutes, 0 a quick stop) are set
 * first. → the days.
 */
export function makeWalkDays(days, dayIndex, placeKeys, { walkId, title, open = false, layOut = false, legMinutes = null, start: startAt = null, stays = null } = {}) {
  const day = days?.[dayIndex];
  const acts = day?.activities || [];
  const byKey = new Map(acts.map(a => [stopKey(a), a]));
  // A stop already inside a walk stays in it: two phones offered the same
  // run ("make these a walk?" — the marks are shared) make one walk, not two
  // pulling its places back and forth.
  const { isInside } = dayWalks(acts);
  const found = (placeKeys || []).map(k => byKey.get(k)).filter(a => a && !isWalk(a) && !isInside(a));
  if (!found.length || !walkId) return days;
  // `stays` (key → minutes): a place's stay inside the walk — 0 a quick stop.
  const stayOf = (a) => (stays && Object.prototype.hasOwnProperty.call(stays, stopKey(a)) ? stays[stopKey(a)] : undefined);
  const withStay = new Map(found.map((a) => {
    const stay = stayOf(a);
    if (stay === undefined) return [a, a];
    return [a, stay > 0
      ? { ...a, quickStop: false, duration: stay, durationHours: undefined, durationMinutes: undefined }
      : { ...a, quickStop: true, duration: null, durationHours: undefined, durationMinutes: undefined }];
  }));
  const places = found.map(a => withStay.get(a));
  const start = Number.isFinite(startAt) ? startAt : (walkSpan(places)?.start ?? null);
  const laid = layOut && start !== null ? new Map(layOutPlaces(start, places, { legMinutes }).map(({ place, patch }) => [place, patch])) : null;
  const placed = places.map(p => (laid ? { ...p, ...laid.get(p) } : p));
  const { walk } = walkFromPlaces(placed, { title, open });
  const walkStop = { ...walk, id: walkId, activityId: walkId, clientActivityId: walkId, scheduledDate: day.date };
  // The walk goes where its earliest place lies on the day.
  const first = Math.min(...found.map(a => acts.indexOf(a)));
  const placedOf = new Map(found.map((a, i) => [a, placed[i]]));
  const next = [];
  acts.forEach((a, i) => {
    if (i === first) next.push(walkStop);
    next.push(placedOf.has(a) ? { ...placedOf.get(a), inWalk: walkId } : a);
  });
  const out = days.slice();
  out[dayIndex] = { ...day, activities: next };
  return out;
}

/** "End the walk": a walk made by "I'm here" stops taking the next one. → the days. */
export function endWalkDays(days, dayIndex, walkKey) {
  const day = days?.[dayIndex];
  const acts = day?.activities || [];
  const walk = acts.find(a => isWalk(a) && stopKey(a) === walkKey);
  if (!walk || (walk.addedVia || walk.added_via) !== 'here') return days;
  const out = days.slice();
  out[dayIndex] = { ...day, activities: acts.map(a => (a === walk ? { ...a, addedVia: null } : a)) };
  return out;
}
